package fr.frux.feature.treeadd.wizard

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.AddAPhoto
import androidx.compose.material.icons.outlined.Image
import androidx.compose.material3.Icon
import androidx.compose.material3.LinearProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.produceState
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.drawBehind
import androidx.compose.ui.geometry.CornerRadius
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.ImageBitmap
import androidx.compose.ui.graphics.PathEffect
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.semantics.LiveRegionMode
import androidx.compose.ui.semantics.Role
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.liveRegion
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import fr.frux.core.designsystem.components.FruxIconButton
import fr.frux.core.designsystem.icons.FruxIcons
import fr.frux.core.media.decodeForPreview
import fr.frux.feature.treeadd.resources.Res
import fr.frux.feature.treeadd.resources.wizard_photo_description
import fr.frux.feature.treeadd.resources.wizard_photo_failed
import fr.frux.feature.treeadd.resources.wizard_photo_percent
import fr.frux.feature.treeadd.resources.wizard_photo_remove
import fr.frux.feature.treeadd.resources.wizard_photo_sending_description
import fr.frux.feature.treeadd.resources.wizard_photo_sending_unknown_description
import fr.frux.feature.treeadd.resources.wizard_photo_waiting
import fr.frux.feature.treeadd.resources.wizard_photo_waiting_description
import fr.frux.feature.treeadd.resources.wizard_photos_add
import fr.frux.feature.treeadd.resources.wizard_photos_add_description
import fr.frux.feature.treeadd.resources.wizard_photos_hint
import fr.frux.feature.treeadd.resources.wizard_photos_progress
import fr.frux.feature.treeadd.resources.wizard_photos_title
import fr.frux.shared.domain.photos.PhotoUpload
import fr.frux.shared.domain.photos.PhotoUploadState
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.withContext
import org.jetbrains.compose.resources.stringResource

private val THUMB_SIZE = 88.dp
private val THUMB_SHAPE = RoundedCornerShape(16.dp)
private const val PERCENT = 100

/** Tag stable de la tuile « Ajouter » (E2E). */
internal const val ADD_PHOTO_TAG = "details_add_photo"

/**
 * Bande de photos de l'étape Détails. [uploads] est non nul pendant l'envoi : les vignettes
 * montrent alors la progression et ne se retirent plus. La section est masquée quand l'appareil
 * n'a aucune source et qu'aucune photo n'est là (desktop).
 */
@Composable
internal fun PhotoStrip(
    photos: List<WizardPhoto>,
    uploads: List<PhotoUpload>?,
    canAdd: Boolean,
    sourcesAvailable: Boolean,
    onAdd: () -> Unit,
    onRemove: (String) -> Unit,
    modifier: Modifier = Modifier,
    removable: Boolean = true,
) {
    if (!sourcesAvailable && photos.isEmpty()) return
    Column(modifier = modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(10.dp)) {
        PhotoStripHeader(photos, uploads)
        val byId = remember(uploads) { uploads?.associateBy { it.id }.orEmpty() }
        LazyRow(horizontalArrangement = Arrangement.spacedBy(10.dp)) {
            items(photos, key = { it.id }) { photo ->
                PhotoThumb(
                    photo = photo,
                    rank = photos.indexOf(photo) + 1,
                    upload = byId[photo.id],
                    removable = removable && uploads == null,
                    onRemove = { onRemove(photo.id) },
                )
            }
            if (canAdd && sourcesAvailable && uploads == null) {
                item(key = "add") { AddPhotoTile(onAdd) }
            }
        }
        if (photos.isEmpty()) {
            Text(
                text = stringResource(Res.string.wizard_photos_hint),
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
        }
    }
}

@Composable
private fun PhotoStripHeader(
    photos: List<WizardPhoto>,
    uploads: List<PhotoUpload>?,
) {
    Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.SpaceBetween) {
        Text(
            text = stringResource(Res.string.wizard_photos_title),
            style = MaterialTheme.typography.titleMedium,
            modifier = Modifier.weight(1f),
        )
        val current =
            uploads?.indexOfFirst { it.state is PhotoUploadState.Sending }?.takeIf { it >= 0 }
                ?: uploads?.indexOfFirst { it.state == PhotoUploadState.Waiting }?.takeIf { it >= 0 }
        if (uploads != null && current != null) {
            Text(
                text = stringResource(Res.string.wizard_photos_progress, current + 1, photos.size),
                style = MaterialTheme.typography.labelMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
                modifier = Modifier.semantics { liveRegion = LiveRegionMode.Polite },
            )
        }
    }
}

@Composable
private fun PhotoThumb(
    photo: WizardPhoto,
    rank: Int,
    upload: PhotoUpload?,
    removable: Boolean,
    onRemove: () -> Unit,
) {
    val decoded by produceState<ImageBitmap?>(null, photo.id) {
        value = withContext(Dispatchers.Default) { photo.image.decodeForPreview() }
    }
    val preview = decoded
    val description = thumbDescription(rank, upload)
    Box(
        modifier =
            Modifier
                .size(THUMB_SIZE)
                .clip(THUMB_SHAPE)
                .background(MaterialTheme.colorScheme.surfaceVariant)
                .semantics { contentDescription = description },
    ) {
        if (preview != null) {
            Image(
                bitmap = preview,
                contentDescription = null,
                contentScale = ContentScale.Crop,
                modifier = Modifier.fillMaxSize(),
            )
        } else {
            Icon(
                imageVector = Icons.Outlined.Image,
                contentDescription = null,
                tint = MaterialTheme.colorScheme.onSurfaceVariant,
                modifier = Modifier.align(Alignment.Center).size(32.dp),
            )
        }
        if (upload != null && upload.state != PhotoUploadState.Sent) UploadVeil(upload.state)
        if (removable) {
            FruxIconButton(
                icon = FruxIcons.Close,
                contentDescription = stringResource(Res.string.wizard_photo_remove, rank),
                onClick = onRemove,
                modifier = Modifier.align(Alignment.TopEnd).testTag("details_remove_photo_$rank"),
            )
        }
    }
}

@Composable
private fun thumbDescription(
    rank: Int,
    upload: PhotoUpload?,
): String =
    when (val state = upload?.state) {
        is PhotoUploadState.Sending -> {
            val fraction = state.fraction
            if (fraction == null) {
                stringResource(Res.string.wizard_photo_sending_unknown_description, rank)
            } else {
                stringResource(Res.string.wizard_photo_sending_description, rank, (fraction * PERCENT).toInt())
            }
        }

        PhotoUploadState.Waiting -> {
            stringResource(Res.string.wizard_photo_waiting_description, rank)
        }

        else -> {
            stringResource(Res.string.wizard_photo_description, rank)
        }
    }

@Composable
private fun UploadVeil(state: PhotoUploadState) {
    Column(
        modifier = Modifier.fillMaxSize().background(Color.Black.copy(alpha = 0.6f)).padding(4.dp),
        verticalArrangement = Arrangement.spacedBy(6.dp, Alignment.CenterVertically),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        when (state) {
            is PhotoUploadState.Sending -> {
                val fraction = state.fraction
                if (fraction != null) {
                    VeilText(stringResource(Res.string.wizard_photo_percent, (fraction * PERCENT).toInt()))
                    LinearProgressIndicator(
                        progress = { fraction },
                        modifier = Modifier.width(56.dp).height(4.dp),
                        color = Color.White,
                        trackColor = Color.White.copy(alpha = 0.3f),
                    )
                } else {
                    LinearProgressIndicator(
                        modifier = Modifier.width(56.dp).height(4.dp),
                        color = Color.White,
                        trackColor = Color.White.copy(alpha = 0.3f),
                    )
                }
            }

            PhotoUploadState.Waiting -> {
                VeilText(stringResource(Res.string.wizard_photo_waiting))
            }

            PhotoUploadState.Failed -> {
                VeilText(stringResource(Res.string.wizard_photo_failed))
            }

            PhotoUploadState.Sent -> {
                Unit
            }
        }
    }
}

@Composable
private fun VeilText(text: String) {
    Text(
        text = text,
        style = MaterialTheme.typography.labelMedium,
        color = Color.White,
        textAlign = TextAlign.Center,
    )
}

@Composable
private fun AddPhotoTile(onAdd: () -> Unit) {
    val outline = MaterialTheme.colorScheme.outline
    val density = LocalDensity.current
    val dashes =
        remember(density) {
            with(density) { PathEffect.dashPathEffect(floatArrayOf(5.dp.toPx(), 4.dp.toPx())) }
        }
    val description = stringResource(Res.string.wizard_photos_add_description)
    Column(
        modifier =
            Modifier
                .size(THUMB_SIZE)
                .clip(THUMB_SHAPE)
                .drawBehind {
                    drawRoundRect(
                        color = outline,
                        size = Size(size.width, size.height),
                        cornerRadius = CornerRadius(16.dp.toPx()),
                        style =
                            Stroke(
                                width = 1.5.dp.toPx(),
                                pathEffect = dashes,
                            ),
                    )
                }.clickable(role = Role.Button, onClick = onAdd)
                .semantics { contentDescription = description }
                .testTag(ADD_PHOTO_TAG),
        verticalArrangement = Arrangement.spacedBy(4.dp, Alignment.CenterVertically),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        Icon(
            imageVector = Icons.Outlined.AddAPhoto,
            contentDescription = null,
            tint = MaterialTheme.colorScheme.primary,
            modifier = Modifier.size(24.dp),
        )
        Text(
            text = stringResource(Res.string.wizard_photos_add),
            style = MaterialTheme.typography.labelMedium,
            color = MaterialTheme.colorScheme.primary,
        )
    }
}
